import * as React from 'react';
import {WithStyles, withStyles} from '@material-ui/core';
import {Done, EventOutlined, PersonOutline} from '@material-ui/icons';
import clsx from 'clsx';
import {IFamilyMember} from '@app-types/websocket/family.types';
import {ITodo, RENEWAL} from '@app-types/websocket/todo.types';
import ImgAvatar from '@components/common/avatar/ImgAvatar';
import Button from '@components/common/button/Button';
import TranslateNew from '@translate/TranslateNew';
import {styleColors} from 'src/main.style';
import {TodoListRowStyle} from './TodoListRow.style';
import {useTodoSeen} from './useTodoSeen';

const tn = TranslateNew.getInstance();
export const TODO_HIDE_DELAY = 700;

interface IProps extends WithStyles<typeof TodoListRowStyle> {
  todo: ITodo;
  family: IFamilyMember[];
  myAccountId: number;
  seen: boolean;
  source?: 'todo' | 'calendar';
  active?: boolean;
  markSeen: () => void;
  readRevision?: string;
  due: Date | null;
  dateLabel?: string;
  animateHide?: boolean;
  style?: React.CSSProperties;
  rowRef?: (element: HTMLDivElement | null) => void;
  edit: () => void;
  complete: (completed: boolean) => void;
  hide?: () => void;
}

const TodoListRow = (props: IProps) => {
  const {classes, todo, family, due} = props;
  const row = React.useRef<HTMLDivElement | null>(null);
  const {visible, recentlySeen} = useTodoSeen(row, {
    todo, myAccountId: props.myAccountId, active: props.active !== false && !props.animateHide,
    seen: props.seen, onSeen: props.markSeen, revisionKey: props.readRevision,
  });
  const calendar = props.source === 'calendar';
  const assigneeId = `${calendar ? 'event' : 'todo'}-assignee-${todo.id}`;
  const completed = !!todo.completedBy;
  const canHide = !calendar && completed && !!props.hide && props.active !== false;
  const [hideButtonReady, setHideButtonReady] = React.useState(false);
  const showHideButton = canHide && hideButtonReady;

  React.useEffect(() => {
    // Keep the button's width stable while the whole row collapses.
    if (props.animateHide) {
      return;
    }
    setHideButtonReady(false);
    if (!canHide) {
      return;
    }
    const timer = window.setTimeout(() => setHideButtonReady(true), TODO_HIDE_DELAY);
    return () => window.clearTimeout(timer);
  }, [canHide, props.animateHide, todo.id, todo.completionTime]);

  const assigned = todo.assigned || [];
  const assignees = assigned.map(id => family.find(member => member.id === id))
    .filter((member): member is IFamilyMember => member !== undefined);
  const assigneeNames = assigned.length > 0 ? tn.familyMemberNames(assigned, family)
    : calendar ? tn.lang.todoCalendarNoPeople : tn.lang.todoUnassigned;
  const completionText = !calendar && todo.completedBy
    ? tn.lang.todoCompletedBy(
      todo.completionTime !== undefined ? tn.formatDateMonthShort(new Date(todo.completionTime)) : undefined,
      tn.familyMemberName(todo.completedBy, family))
    : null;
  const tomorrow = new Date(Date.now());
  tomorrow.setHours(24, 0, 0, 0);
  const dateDue = due && !completed && due.getTime() < tomorrow.getTime();
  // Reserve the completion footer before checking a todo, even without a due date.
  const hasFooterStatus = !calendar || due !== null;

  return (
    <div ref={element => {row.current = element; if (props.rowRef) {props.rowRef(element);}}} style={props.style}
      data-todo-id={calendar ? undefined : todo.id}
      data-event-id={calendar ? todo.id : undefined}
      data-read-state={props.seen ? (recentlySeen ? 'just-seen' : 'seen') : 'unseen'}
      className={clsx(classes.row, visible && !props.seen && classes.visibleUnseen,
        recentlySeen && classes.recentlySeen, showHideButton && classes.hideButtonVisible,
        props.animateHide && classes.hiding)}>
      <Button type="button" classes={{root: classes.details, label: classes.detailsLabel}} onClick={props.edit}
        disabled={props.animateHide}
        aria-label={`${tn.lang.edit}: ${todo.title}`} aria-describedby={assigneeId}>
        <span className={classes.avatarRail}>
          {assignees.length > 0 ? (
            <span className={classes.avatarGroup}>
              {assignees.slice(0, 2).map((member, index) => (
                <ImgAvatar key={member.id} altText={member.firstName} color={member.color || '#FFFFFF'}
                  imgName={member.avatar} imgUrl={member.imageAvatar ? member.avatarURL : undefined}
                  style={{
                    width: assignees.length === 1 ? 52 : 38,
                    height: assignees.length === 1 ? 52 : 38,
                    marginLeft: index > 0 ? -16 : 0,
                    border: `2px solid ${styleColors.white}`,
                    boxSizing: 'border-box',
                  }}
                />
              ))}
              {assignees.length > 2 && <span className={classes.extraAssignees}>+{assignees.length - 2}</span>}
            </span>
          ) : (
            <span className={classes.unassignedAvatar}><PersonOutline /></span>
          )}
        </span>
        <span className={classes.body}>
          <span className={clsx(classes.title, !props.seen && !completed && classes.unseenTitle,
            completed && classes.completedTitle)}>{todo.title}</span>
          <span className={classes.assignee} id={assigneeId}>
            {tn.lang.todoAssigned}: <span className={classes.assigneeNames}>{assigneeNames}</span>
          </span>
          {todo.description && (
            <span className={classes.preview} title={todo.description}>
              {todo.description}
            </span>
          )}
          <span className={clsx(classes.metadata, hasFooterStatus && classes.metadataWithDate)}>
            {todo.renewal && (
              <span>{todo.renewal === RENEWAL.NONE ? tn.lang.todoRenewNo : tn.lang.todoRenewHandover}</span>
            )}
            {props.seen ? (
              <span className={classes.seenBy}>
                {tn.lang.todoSeenBy}
                {(todo.seen || []).map(id => {
                  const member = family.find(person => person.id === id);
                  return member ? (
                    <ImgAvatar key={id} altText={member.firstName} color={member.color || '#FFFFFF'}
                      imgName={member.avatar} imgUrl={member.imageAvatar ? member.avatarURL : undefined}
                      style={{width: 10, height: 10}} />
                  ) : null;
                })}
              </span>
            ) : <span className={classes.unseen}>{tn.lang.todoUnseen}</span>}
            {completionText ? (
              <span className={classes.due}>{completionText}</span>
            ) : due && (
              <span className={clsx(classes.due, dateDue && classes.dateDue)}>
                <EventOutlined />{props.dateLabel || tn.lang.todoDue(tn.formatDateMonthShort(due))}
              </span>
            )}
          </span>
        </span>
      </Button>
      <div className={clsx(classes.actions, hasFooterStatus && classes.actionsWithDate)}>
        <label className={classes.completionControl}>
          <input type="checkbox" className={classes.completionInput} checked={completed}
            disabled={props.animateHide}
            aria-label={completed ? tn.lang.todoMarkIncomplete(todo.title) : tn.lang.todoMarkComplete(todo.title)}
            onChange={() => props.complete(!completed)} />
          <span className={clsx(classes.completionCircle, completed && classes.completionChecked)} aria-hidden="true">
            {completed && <Done />}
          </span>
        </label>
        {!calendar && props.hide && (
          <div className={classes.hideButtonSlot}>
            {showHideButton && (
              <Button type="button" variant="outlined" color="primary" size="small"
                className={classes.hideButton} onClick={props.hide}
                disabled={props.animateHide}
                aria-label={`${tn.lang.todoHide}: ${todo.title}`}>
                {tn.lang.todoHide}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default withStyles(TodoListRowStyle)(TodoListRow);



// WEBPACK FOOTER //
// ./src/components/todo/TodoListRow.tsx